import axioslib, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { AuthInterceptor } from "./AuthInterceptor";
import VersionInterceptor from "@/api/VersionInterceptor";
import { Logger } from "@iloq/shared";
import {
  ISolutionArticleSearchQuery,
  ISolutionArticleSearchResult,
  ISolutionCategoryAndFolderStructure,
  ISupportRequestInput,
  ISupportRequestConversationItemInput,
  ISupportRequestConversationItem,
  ISupportRequest,
  ISupportRequestFilters,
} from "@iloq/shared";

export class TicketClosedError extends Error {}

export default class SolutionsApi {
  private readonly axios: AxiosInstance;

  constructor() {
    this.axios = axioslib.create();
    AuthInterceptor.setAuthInterceptors(this.axios);
    VersionInterceptor.setVersionInterceptor(this.axios);
  }

  searchArticles(query: ISolutionArticleSearchQuery): Promise<ISolutionArticleSearchResult> {
    Logger.debug(`Searching for articles with query= ${JSON.stringify(query)}`);

    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/support/solutions/search",
      data: query,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.searchArticles failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("SolutionsApi.searchArticles result:", result);
        return result.data as ISolutionArticleSearchResult;
      });
  }

  fetchCategoryAndFolderStructure(): Promise<ISolutionCategoryAndFolderStructure> {
    Logger.debug("Fetching category & folder structure for solutions");

    const options: AxiosRequestConfig = {
      method: "GET",
      url: "/api/v1/support/solutions/structure",
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.fetchCategoryAndFolderStructure failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("SolutionsApi.fetchCategoryAndFolderStructure result:", result);
        return result.data as ISolutionCategoryAndFolderStructure;
      });
  }

  searchSupportTickets(
    filters: ISupportRequestFilters
  ): Promise<{
    values: ISupportRequest[];
    count: number;
  }> {
    Logger.debug("Fetching support tickets");

    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/support/tickets",
      data: filters,
    };

    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.fetchSupportTickets failed:", error);
        return Promise.reject(error);
      })
      .then(
        (
          result: AxiosResponse<{
            values: ISupportRequest[];
            count: number;
          }>
        ) => {
          Logger.debug("SolutionsApi.fetchSupportTickets result:", result);
          return result.data;
        }
      );
  }

  createSupportTicket(ticket: ISupportRequestInput): Promise<any> {
    Logger.debug("Creating support ticket");
    const formData = new FormData();
    const { attachments } = ticket;

    attachments?.forEach((file) => {
      formData.append("attachments", file, file.name);
    });

    formData.append("ticket", JSON.stringify(ticket));

    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/support/createSupportTicket",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.createSupportTicket failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("SolutionsApi.createSupportTicket result:", result);
        return result.data;
      });
  }

  fetchConversation({
    ticketId,
    afterDate,
    partnerId,
  }: {
    ticketId: string;
    afterDate: string;
    partnerId: string;
  }): Promise<ISupportRequestConversationItem[]> {
    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/support/fetchConversation",
      data: {
        ticketId,
        afterDate,
        partnerId,
      },
    };

    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.fetchConversation failed:", error);

        return Promise.reject(error);
      })
      .then((result: AxiosResponse<ISupportRequestConversationItem[]>) => {
        Logger.debug("SolutionsApi.fetchConversation result:", result);
        return result.data;
      });
  }

  submitReply(reply: ISupportRequestConversationItemInput): Promise<any> {
    const { attachments, ...rest } = reply;
    const formData = new FormData();

    attachments?.forEach((file) => {
      formData.append("attachments", file, file.name);
    });

    formData.append("reply", JSON.stringify(rest));

    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/support/submitReply",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    return this.axios(options)
      .catch((error) => {
        if (error.response?.status === 409) {
          Logger.debug("SolutionsApi.submitReply got 409 response (ticket closed, cannot reply)");
          return Promise.reject(new TicketClosedError());
        }

        Logger.error("SolutionsApi.submitReply failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("SolutionsApi.submitReply result:", result);
        return result.data;
      });
  }

  fetchTicketAttachmentURL({
    ticketId,
    attachmentId,
  }: {
    ticketId: string;
    attachmentId: string;
  }): Promise<string> {
    Logger.debug("Fetching support ticket file attachment url");

    const options: AxiosRequestConfig = {
      method: "GET",
      url: `/api/v1/support/ticket/${ticketId}/attachment/${attachmentId}`,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.fetchTicketAttachmentURL failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("SolutionsApi.fetchTicketAttachmentURL result:", result);
        return result.data;
      });
  }

  fetchConversationAttachmentURL({
    ticketId,
    messageId,
    attachmentId,
  }: {
    ticketId: string;
    messageId: string;
    attachmentId: string;
  }): Promise<string> {
    Logger.debug("Fetching support conversation message file attachment url");

    const options: AxiosRequestConfig = {
      method: "GET",
      url: `/api/v1/support/ticket/${ticketId}/conversation/${messageId}/attachment/${attachmentId}`,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.fetchConversationAttachmentURL failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("SolutionsApi.fetchConversationAttachmentURL result:", result);
        return result.data;
      });
  }

  fetchSolutionAttachmentURL({
    articleId,
    languageCode,
    attachmentId,
  }: {
    articleId: string;
    languageCode: string;
    attachmentId: string;
  }): Promise<string> {
    Logger.debug("Fetching solution article file attachment url");

    const options: AxiosRequestConfig = {
      method: "GET",
      url: `/api/v1/support/solutions/${articleId}/language/${languageCode}/attachment/${attachmentId}`,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("SolutionsApi.fetchSolutionAttachmentURL failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("SolutionsApi.fetchSolutionAttachmentURL result:", result);
        return result.data;
      });
  }
}
